import {
  GetThirdPartyAddresListByTpidParams,
  TpAddressResponse2,
  getThirdPartyAddresListByTpid,
  tpAddressChildrenResponse,
} from 'services/thirdParty';
import React, { useState } from 'react';
import { Select, SelectProps, Typography } from 'antd';

import { noop } from 'lodash';
import { useQuery } from 'react-query';

export type AddresseListSelectProps = SelectProps<any> & {
  value?: any;
  params?: GetThirdPartyAddresListByTpidParams;
  onChange?: (address: any) => void;
  onSelected?: (address: any) => void;
  disabled?: boolean;
  allowClear?: boolean;
  display?: boolean;
  displayStyle?: React.CSSProperties;
  data?: any[];
  // Add this new prop to handle updating parent address
  onUpdateParentAddress?: (childAddress: any) => void;
};

const AddresseListSelect = ({
  value: externalValue,
  params,
  onChange = noop,
  onSelected,
  onUpdateParentAddress,
  display,
  data,
  displayStyle,
  ...otherProps
}: AddresseListSelectProps) => {
  // Internal state for when no external value/onChange is provided
  const [internalValue, setInternalValue] = useState<string | undefined>();
  
  // Use external value if provided, otherwise use internal state
  const currentValue = externalValue !== undefined ? externalValue : internalValue;
  
  // Create unique options with generated IDs
  const uniqueOptions = React.useMemo(() => {
    if (!data) return [];
    
    return data.map((item, index) => ({
      ...item,
      // Create a unique identifier combining city and index for duplicates
      uniqueId: `${item.tpmainadrcity}-${item.tpmainadrcitylabel}-${index}`,
      displayText: `${item.tpmainadrcitylabel} - ${item.tpmainadrstreet1}`,
    }));
  }, [data]);

  const handleChange: SelectProps<any>['onChange'] = (selectedValue, option) => {
    // Update internal state if no external onChange provided
    if (externalValue === undefined) {
      setInternalValue(selectedValue);
    }
    
    // Find the selected item
    const selectedItem = uniqueOptions.find(item => item.uniqueId === selectedValue);
    
    // Call external handlers if provided
    onChange(selectedItem);
    if (onSelected && selectedItem) {
      onSelected(selectedItem);
    }

    // NEW: Call the parent address update callback
    if (onUpdateParentAddress && selectedItem) {
      onUpdateParentAddress(selectedItem);
    }

    // Log for debugging
    console.log('Selected item:', selectedItem);
  };

  // Normalize the current value to work with our unique IDs
  const normalizedValue = React.useMemo(() => {
    if (!currentValue) return undefined;
    
    // If it's already a uniqueId, use it
    if (typeof currentValue === 'string' && currentValue.includes('-')) {
      return currentValue;
    }
    
    // If it's an object, try to find matching option
    if (typeof currentValue === 'object') {
      const matchingOption = uniqueOptions.find(option => 
        option.tpmainadrcity === currentValue.tpmainadrcity &&
        option.tpmainadrcitylabel === currentValue.tpmainadrcitylabel &&
        option.tpmainadrstreet1 === currentValue.tpmainadrstreet1
      );
      return matchingOption?.uniqueId;
    }
    
    return currentValue;
  }, [currentValue, uniqueOptions]);

  if (display) {
    const selectedItem = uniqueOptions.find(
      (option) => option.uniqueId === normalizedValue
    );
    
    return (
      <Typography.Text style={displayStyle}>
        {selectedItem?.displayText || ''}
      </Typography.Text>
    );
  }

  return (
    <Select
      onChange={handleChange}
      value={normalizedValue}
      allowClear
      placeholder="Select an address"
      {...otherProps}
    >
      {uniqueOptions.map((option) => (
        <Select.Option value={option.uniqueId} key={option.uniqueId}>
          {option.displayText}
        </Select.Option>
      ))}
    </Select>
  );
};

export default AddresseListSelect;